// MUVSubGrowerTable.jsx
import React, { useState, useContext } from "react";
import { useTranslation } from 'react-i18next';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import "../ManageUnannouncedVisitor/MUVTable.css";
import { SnackbarContext } from "context/snackBarContext";
import { AuthData } from "auth/AuthWarpper";
import Service from "../../../services/ManageUnannouncedVisitorSubGrowerService";
import WarningModal from '../../../utils/WarningModal';
import dayjs from 'dayjs';

const SUB_GROWER_ASSIGNED_TO_OTHER_MESSAGE =
    "The selected Sub Grower is assigned to other unannounced visitor. Please contact the unannounced visitor to unassign the Sub Grower or wait the expired time for the Sub Grower to be automatically unassigned.";

const FARMER_ASSIGNED_TO_OTHER_MESSAGE =
    "The corresponding Farmer is already assigned to another UV. Therefore, this Sub-grower cannot be assigned to you.";

const MUVSubGrowerTable = ({ data, onAssignmentUpdate ,cropYear}) => {
    const { t } = useTranslation();
    const { setOpenAlert, setSeverity, setMessage } = useContext(SnackbarContext);
    const { user } = AuthData();
    const [loading, setLoading] = useState(false);
    const [warningMessage, setWarningMessage] = useState("");

     const moduleName = "Unannounced Visit";
      const functionalityNames = [
        "Manage Unannounced Visit - Sub Grower",
        "Manage Unannounced Visitor - Sub Grower",
      ];
    
      const read_only_user = user?.accessibleModules
        ?.find(module => module.module_name === moduleName)
        ?.accessible_functionalities
        ?.find(func => functionalityNames.includes(func.functionality_name))
        ?.read_only;

    const [rowAssignmentStatus, setRowAssignmentStatus] = useState({});

    const getRowKey = (item) => item.subGrowerId || item.permanentCode || item.temporaryCode;

    const buildAssignmentPayload = (item, isAssigning) => ({
        batOneId: isAssigning ? user?.batOneId : null,
        isAssigned: isAssigning,
        cropYear: cropYear,
        subGrowerId: item.subGrowerId,
        isFarmerAssigned: item.isFarmerAssigned,
        ...(isAssigning
            ? { assignedDate: dayjs().format('YYYY-MM-DD') }
            : { unAssignedDate: dayjs().format('YYYY-MM-DD') }),
    });

    // New helper function to check if a row should be disabled
    const isRowDisabled = (item) => {
        const disabledStatuses = ['inprogress', 'in progress', 're-opened', 'reopened', 'approved'];
        return disabledStatuses.includes(item.surveyStatus?.toLowerCase());
    };

    // Farmer of this sub grower is already assigned to a different user
    const isFarmerAssignedToOther = (item) =>
        item.isFarmerAssigned === true &&
        String(item.farmerAssignedToUserId || '').toLowerCase() !== String(user?.userId || '').toLowerCase();

    const handleAssignment = async (rowKey, isAssigning) => {
        try {
            const item = data.find(d => getRowKey(d) === rowKey);

            // Check if trying to unassign someone else's assignment
            if (!isAssigning && item.isAssigned && item.batOneId !== user?.batOneId) {
                setSeverity("warning");
                setMessage("The selected Sub Grower is assigned to other unannounced visitor. Please contact the unannounced visitor to unassign the Sub Grower or wait the expired time for the Sub Grower to be automatically unassigned.");
                setOpenAlert(true);
                return;
            }

            setLoading(true);
            const updatePayload = [buildAssignmentPayload(item, isAssigning)];

            const response = await Service.updateVisitorAssignment(updatePayload);

            if (response?.data) {
                setSeverity("success");
                setMessage(isAssigning ? "Successfully assigned visitor" : "Successfully unassigned visitor");
                setOpenAlert(true);

                // Update local assignment status for this row only
                setRowAssignmentStatus(prev => ({
                    ...prev,
                    [rowKey]: isAssigning
                }));
            }
        } catch (error) {
            console.error("Error updating assignment:", error);
            setSeverity("error");
            setMessage(error?.response?.data?.message || error?.message);
            setOpenAlert(true);
        } finally {
            setLoading(false);
        }
    };

    // Add or update handleRowCheckboxChange function
    const handleRowCheckboxChange = (event, rowKey) => {
        const isChecked = event.target.checked;
        const item = data.find(d => getRowKey(d) === rowKey);

        // Unchecking a sub grower assigned to another visitor
        if (!isChecked && item.isAssigned && item.batOneId !== user?.batOneId) {
            setWarningMessage(SUB_GROWER_ASSIGNED_TO_OTHER_MESSAGE);
            return;
        }

        // Checking a sub grower whose farmer is already assigned to another visitor
        if (isChecked && isFarmerAssignedToOther(item)) {
            setWarningMessage(FARMER_ASSIGNED_TO_OTHER_MESSAGE);
            return;
        }

        handleAssignment(rowKey, isChecked);
    };

    return (
        <>
            <div className="table-container">
                <table className="muv-table">
                    <thead>
                        <tr>
                            <th className="checkbox-header"></th>
                            <th>
                                <div>
                                    {t('Property No.')}
                                </div>
                            </th>
                            <th>
                                <div>
                                    {t('Grower No.')}
                                </div>
                            </th>
                            <th className="actions-header">
                                <div>
                                    {t('Farmer Name')}
                                </div>
                            </th>
                            <th className="actions-header">
                                <div>
                                    {t('Sub Grower Code')}
                                </div>
                            </th>
                            <th className="actions-header">
                                <div>
                                    {t('Sub Grower Name')}
                                </div>
                            </th>
                            <th className="actions-header">
                                <div>
                                    {t('Survey Status')}
                                </div>
                            </th>
                            <th className="actions-header">
                                <div>
                                    {t('Status')}
                                </div>
                            </th>
                            <th className="actions-header">
                                <div>
                                    {t('U.V. Name')}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, index) => (
                            <tr 
                                key={index} 
                                className={isRowDisabled(item) ? 'disabled-row' : ''}
                                style={{ 
                                    
                                    pointerEvents: isRowDisabled(item) ? 'none' : 'auto'
                                }}
                            >
                                <td className="checkbox-cell">
                                    <Checkbox
                                        checked={
                                            rowAssignmentStatus[getRowKey(item)] !== undefined 
                                                ? rowAssignmentStatus[getRowKey(item)] 
                                                : item.isAssigned
                                        }
                                        onChange={(e) => handleRowCheckboxChange(e, getRowKey(item))}
                                        disabled={
                                            loading || read_only_user||
                                            isRowDisabled(item)
                                        }
                                        title={
                                            isRowDisabled(item)
                                                ? "Cannot assign due to current survey status"
                                                : (item.isAssigned && item.batOneId !== user?.batOneId
                                                    ? "Assigned to another visitor"
                                                    : "")
                                        }
                                    />
                                </td>
                                <td>{item.propertyFarmNumber}</td>
                                <td className="grower-number-cell">
                                    {item.allGrowerNumber}
                                </td>
                                <td>{item.allFarmerName}</td>
                                <td>{item.permanentCode || item.temporaryCode || '-'}</td>
                                <td>{item.subGrowerName || '-'}</td>
                                <td>{item.surveyStatus}</td>
                                <td>{item.status || (item.subGrowerStatus ? 'Active' : 'Inactive')}</td>
                                <td>{item.userName || '-'}</td>
                            </tr>
                        ))}
                        {data.length === 0 && (
                            <tr>
                                <td colSpan={9} style={{ textAlign: 'center' }}>
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {loading && (
                    <div style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bottom: 0, 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)'
                    }}>
                        <CircularProgress />
                    </div>
                )}
            </div>
            <WarningModal
                open={Boolean(warningMessage)}
                onClose={() => setWarningMessage("")}
                message={warningMessage}
            />
        </>
    );
};

export default MUVSubGrowerTable;
